import React, { useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Typography,
  TextField,
} from '@mui/material';

function ChatPanel({ chatMessages, message, setMessage, handleSend, isLoading, chatPanelWidth, handleMouseDown, isResizing }) {
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
      <Box
      sx={{
        width: { xs: '100%', md: chatPanelWidth || 350 },
        minWidth: { xs: '100%', md: 300 }, // Minimum width for chat panel
        maxWidth: { md: `calc(100% - 400px)` }, // Ensure calendar always has at least 400px
        borderRight: '1px solid rgba(255, 255, 255, 0.05)',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'rgba(15, 23, 42, 0.6)', // slate-900/60  
        backdropFilter: 'blur(8px)', // backdrop-blur-sm
        flexShrink: 0,
        position: 'relative',
        userSelect: isResizing ? 'none' : 'auto', // Prevent text selection while resizing
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)', // shadow-2xl shadow-black/50
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2, // p-4 from reference
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2.5, // gap-2.5 from reference
          bgcolor: 'rgba(255, 255, 255, 0.02)', // white/[0.02]
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                width: 32, // w-8
                height: 32, // h-8
                borderRadius: '0.5rem', // rounded-lg
                background: 'linear-gradient(to bottom right, #7C3AED 0%, #4F46E5 100%)', // from-violet-600 to-indigo-600
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
            </svg>
          </Box>
          <Box
            sx={{
              position: 'absolute',
              bottom: -4, // -bottom-1 from reference
              right: -4,  // -right-1 from reference
            }}
          >
            <span style={{ position: 'relative', display: 'flex', height: '10px', width: '10px' }}>
              <span style={{
                animation: 'ping 1s cubic-bezier(0, 0, 0.2, 1) infinite',
                position: 'absolute',
                display: 'inline-flex',
                height: '100%',
                width: '100%',
                borderRadius: '9999px',
                backgroundColor: '#4ADE80', // green-400
                opacity: 0.75,
              }} />
              <span style={{
                position: 'relative',
                display: 'inline-flex',
                borderRadius: '9999px',
                height: '10px',
                width: '10px',
                backgroundColor: '#22C55E', // green-500
              }} />
            </span>
          </Box>
        </Box>
        <Box>
          <Box sx={{ fontSize: '0.875rem', fontWeight: 700, color: 'white', fontFamily: 'system-ui, -apple-system, sans-serif' }}> {/* text-sm font-bold */}
            Chat with EVA
          </Box>
          <Box sx={{ fontSize: '0.625rem', color: '#94A3B8', fontFamily: 'system-ui, -apple-system, sans-serif' }}> {/* text-[10px] text-slate-400 */}
            Enterprise Virtual Assistant
          </Box>
        </Box>
      </Box>
      </Box>

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2, // p-4 from reference
          display: 'flex',
          flexDirection: 'column',
          gap: 1, // space-y-6 from reference
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        {chatMessages?.length > 0 ? (
          <>
            {chatMessages.map((msg, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  gap: 1.5, // gap-3 from reference
                  alignItems: 'flex-start',
                  alignSelf: msg.type === 'user' ? 'flex-end' : 'flex-start',
                  flexDirection: msg.type === 'user' ? 'row-reverse' : 'row',
                }}
              >
                {/* Avatar */}
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    bgcolor: msg.type === 'user' ? '#1E2442' : 'transparent',
                    background: msg.type === 'eva' ? 'linear-gradient(135deg, #5B7FFF 0%, #7B5FFF 100%)' : undefined,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: msg.type === 'user' ? '#8B92B2' : 'white',
                    flexShrink: 0,
                  }}
                >
                  {msg.type === 'user' ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 8V4H8"/>
                      <rect width="16" height="12" x="4" y="8" rx="2"/>
                      <path d="M2 14h2"/>
                      <path d="M20 14h2"/>
                      <path d="M15 13v2"/>
                      <path d="M9 13v2"/>
                    </svg>
                  )}
                </Box>

                <Box
                  sx={{
                    maxWidth: '90%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5, // space-y-1
                    textAlign: 'left',
                  }}
                >
                  {/* Message Bubble */}
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: msg.type === 'user' ? '#1E2442' : '#1E2442',
                      color: 'white',
                      borderRadius: 2,
                      borderTopRightRadius: msg.type === 'user' ? 0 : 2,
                      borderTopLeftRadius: msg.type === 'eva' ? 0 : 2,
                      border: '1px solid #2A2F4A',
                      fontSize: '0.9rem',
                      lineHeight: 1.5,
                      fontFamily: '"Roboto Condensed", sans-serif',
                      minWidth: 0,
                      wordBreak: 'break-word', // Ensure long words/links wrap
                      overflowWrap: 'break-word',
                    }}
                  >
                    {msg.isThinking ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: 'inherit', fontStyle: 'italic', color: '#8B92B2' }}>
                          Thinking
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {[0, 1, 2].map((i) => (
                            <Box
                              key={i}
                              sx={{
                                width: 4,
                                height: 4,
                                borderRadius: '50%',
                                bgcolor: '#8B92B2',
                                animation: 'pulse 1.4s ease-in-out infinite',
                                animationDelay: `${i * 0.2}s`,
                                '@keyframes pulse': {
                                  '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                                  '40%': { opacity: 1, transform: 'scale(1.2)' },
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    ) : typeof msg.value === 'string' ? (
                      msg.type === 'eva' ? (
                        <Box
                          sx={{
                            '& p': { margin: '0 0 8px 0' },
                            '& p:last-child': { margin: 0 },
                            '& ul, & ol': { margin: '8px 0', paddingLeft: '20px' },
                            '& li': { margin: '4px 0' },
                            '& code': {
                              display: 'inline-block',
                              p: 1.5, // p-3 from reference
                              borderRadius: '1rem', // rounded-2xl
                              fontSize: { xs: '0.75rem', sm: '0.875rem' }, // text-xs sm:text-sm
                              lineHeight: 1.75, // leading-relaxed
                              bgcolor: 'rgba(255, 255, 255, 0.05)', // bg-white/5
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              color: '#CBD5E1', // text-slate-200
                              borderTopLeftRadius: 0, // rounded-tl-none for EVA messages
                            },
                          }}
                        >
                          <ReactMarkdown
                            components={{
                              a: ({ node, children, ...props }) => (
                                <a
                                  {...props}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: 'white', cursor: 'pointer', textDecoration: 'underline' }}
                                >
                                  {children}
                                </a>
                              ),
                            }}
                          >
                            {msg.value}
                          </ReactMarkdown>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: 'inherit' }}>
                          {msg.value}
                        </Typography>
                      )
                    ) : (
                      <Box sx={{ fontSize: 'inherit' }}>{msg.value}</Box>
                    )}
                  </Box>
                  
                  {/* Timestamp */}
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.625rem', // text-[10px] from reference
                      color: '#64748B', // text-slate-500 from reference
                      fontWeight: 500, // font-medium
                      mt: 0.5, // mt-1
                      alignSelf: msg.type === 'user' ? 'flex-end' : 'flex-start',
                    }}
                  >
                    {msg.timestamp || (() => {
                      const now = new Date();
                      let hours = now.getHours();
                      const minutes = now.getMinutes();
                      const ampm = hours >= 12 ? 'PM' : 'AM';
                      hours = hours % 12;
                      hours = hours ? hours : 12;
                      const minutesStr = minutes < 10 ? '0' + minutes : minutes;
                      return `${hours}:${minutesStr} ${ampm}`;
                    })()}
                  </Typography>
                </Box>
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#8B92B2',
              fontSize: { xs: '0.875rem', sm: '0.9rem' },
              textAlign: 'center',
              p: 2,
              fontFamily: '"Roboto Condensed", sans-serif',
            }}
          >
            Start a conversation with EVA...
          </Box>
        )}
      </Box>

      {/* Chat Input */}
      <Box
        sx={{
          p: 2, // p-4 from reference
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
          bgcolor: 'rgba(2, 6, 23, 0.5)', // bg-slate-950/50
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <TextField
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask EVA to reschedule..."
            multiline
            rows={3}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255, 255, 255, 0.05)', // bg-white/5
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '0.75rem', // rounded-xl
                pl: 2, // pl-4
                pr: 6, // pr-12
                py: 1.5, // py-3
                fontSize: '0.875rem', // text-sm
                color: 'white',
                height: '80px', // h-[80px] from reference
                '& textarea': {
                  '&::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none',
                },
                '& fieldset': {
                  border: 'none',
                },
                '&:hover fieldset': {
                  border: 'none',
                },
                '&.Mui-focused': {
                  border: '1px solid #8B5CF6', // focus:border-violet-500
                  boxShadow: '0 0 0 1px #8B5CF6', // focus:ring-1 focus:ring-violet-500
                },
                '& textarea::placeholder': {
                  color: '#64748B', // placeholder-slate-500
                  opacity: 1,
                },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              right: 8, // right-2
              bottom: 8, // bottom-2
              display: 'flex',
              alignItems: 'center',
              gap: 0.5, // gap-1
            }}
          >
            <Box
              onClick={handleSend}
              sx={{
                height: 32, // h-8
                width: 32, // w-8
                p: 0,
                bgcolor: '#8B5CF6', // bg-violet-600
                color: 'white',
                borderRadius: '0.5rem', // rounded-lg
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.5 : 1,
                transition: 'all 0.2s ease',
                boxShadow: '0 10px 15px -3px rgba(139, 92, 246, 0.2)', // shadow-lg shadow-violet-600/20
                '&:hover': {
                  bgcolor: '#7C3AED', // hover:bg-violet-500
                  transform: 'scale(1.05)',
                },
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 2L11 13"/>
                <path d="M22 2L15 22L11 13L2 9L22 2Z"/>
              </svg>
            </Box>
          </Box>
        </Box>
      </Box>
      
      {/* Resize Handle */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: '4px',
          cursor: 'col-resize',
          bgcolor: 'transparent',
          transition: 'background-color 0.2s',
          '&:hover': {
            bgcolor: 'rgba(139, 92, 246, 0.5)', // violet-500/50
          },
          '&:active': {
            bgcolor: 'rgba(139, 92, 246, 0.8)', // violet-500/80
          },
          zIndex: 10,
        }}
      />
    </Box>
  );
}

export default ChatPanel;
